/* eslint-disable @typescript-eslint/no-explicit-any */
import { BarChart3, Building2, FileText, Palette, Users } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { Organization, OrganizationUpdate, UserRoles } from '../../types';
import organizationService from '../../services/organizationService';
import configurationService from '../../services/configurationService';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/Common/Card';
import { ConfirmationModal } from '@/components/Common/ConfirmationModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Common/tabs';
import { useAuthStore, useIsSuperAdmin } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/Layout/PageLayout';
import {
  OrganizationMembers,
  OrganizationOverview,
  OrganizationTemplates,
} from '@/components/Organizations';
import { CustomizationTab } from './components/CustomizationTab';
import { KnowledgeSharingCard } from './components/KnowledgeSharingCard';

const MyOrganizationPage: React.FC = () => {
  const { user } = useAuthStore();
  const isSuperAdmin = useIsSuperAdmin();
  const { t } = useTranslation();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSectorChangeConfirm, setShowSectorChangeConfirm] = useState(false);
  const [tab, setTab] = useState('overview');

  // Super-admins and organisation admins can edit
  const canEdit = isSuperAdmin || user?.role === UserRoles.Admin;

  // Form state
  const [formData, setFormData] = useState<OrganizationUpdate>({
    name: '',
    description: '',
    domain: '',
    sector_template_id: '',
    is_active: true
  });

  useEffect(() => {
    loadOrganization();
  }, []);

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
        description: organization.description || '',
        domain: organization.domain || '',
        sector_template_id: organization.sector_template_id || '',
        is_active: organization.is_active
      });
    }
  }, [organization]);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      setError(null);
      const org = await organizationService.getMyOrganization();
      setOrganization(org);
    } catch (err: any) {
      console.error('Error loading organization:', err);
      if (err.response?.status === 404) {
        setError(t('organizationsPage.notAssociated'));
      } else {
        setError(t('organizationsPage.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveOrganization = async (sectorChanged: boolean) => {
    if (!organization) return;

    try {
      setSaving(true);
      setError(null);
      const updated = await organizationService.updateOrganization(organization.id, formData);
      setOrganization(updated);

      if (sectorChanged && formData.sector_template_id) {
        try {
          await configurationService.loadTemplate(organization.id, formData.sector_template_id);
        } catch (templateError) {
          setError(
            t(
              'organizations.page.sectorChangeTemplateLoadError',
              "Le secteur a bien été modifié, mais la réinitialisation automatique de la configuration a échoué."
            )
          );
          console.error('Error reloading sector template after organization update:', templateError);
        }
      }

      setIsEditing(false);
    } catch (err: any) {
      console.error('Error updating organization:', err);
      setError(err.response?.data?.detail || t('organizationsPage.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!organization) return;

    const sectorChanged = (formData.sector_template_id || '') !== (organization.sector_template_id || '');
    if (sectorChanged) {
      setShowSectorChangeConfirm(true);
      return;
    }

    await saveOrganization(false);
  };

  const handleConfirmSectorChange = async () => {
    setShowSectorChangeConfirm(false);
    await saveOrganization(true);
  };

  const handleCancel = () => {
    if (organization) {
      setFormData({
        name: organization.name,
        description: organization.description || '',
        domain: organization.domain || '',
        sector_template_id: organization.sector_template_id || '',
        is_active: organization.is_active
      });
    }
    setIsEditing(false);
    setError(null);
  };

  const icon = <Building2 className="h-6 w-6" />;

  if (loading) {
    return (
      <PageLayout icon={icon} title={t('sidebar.manageOrganisation')} subtitle="">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageLayout>
    );
  }

  if (error && !organization) {
    return (
      <PageLayout icon={icon} title={t('sidebar.manageOrganisation')} subtitle="">
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive text-base">{t('organizationsPage.error')}</CardTitle>
            <CardDescription className="text-destructive/90">{error}</CardDescription>
          </CardHeader>
        </Card>
      </PageLayout>
    );
  }

  if (!organization) {
    return (
      <PageLayout icon={icon} title={t('sidebar.manageOrganisation')} subtitle="">
        <Card>
          <CardHeader>
            <CardTitle>{t('organizationsPage.noOrganization')}</CardTitle>
            <CardDescription>{t('organizationsPage.notAssociated')}</CardDescription>
          </CardHeader>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      icon={icon}
      title={organization.name}
      subtitle={organization.description || ''}
    >
      {error && (
        <Card className="border-destructive/40 mb-4">
          <CardHeader>
            <CardTitle className="text-destructive text-base">{t('organizationsPage.error')}</CardTitle>
            <CardDescription className="text-destructive/90">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <Tabs defaultValue="overview" value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex w-full space-x-2 rounded-xl bg-muted p-1 h-auto mb-4">
          <TabsTrigger
            value="overview"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <BarChart3 className="h-4 w-4" />
            {t('organizationDetails.tabs.overview', 'Aperçu')}
          </TabsTrigger>
          <TabsTrigger
            value="customization"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <Palette className="h-4 w-4" />
            {t('organizationDetails.tabs.customization', 'Personnalisation')}
          </TabsTrigger>
          <TabsTrigger
            value="members"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <Users className="h-4 w-4" />
            {t('organizationDetails.tabs.members', 'Membres')}
          </TabsTrigger>
          <TabsTrigger
            value="templates"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <FileText className="h-4 w-4" />
            {t('organizationDetails.tabs.templates', 'Templates')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OrganizationOverview
            organization={organization}
            canEdit={canEdit}
            isEditing={isEditing}
            formData={formData}
            saving={saving}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
            onCancel={handleCancel}
            onFormChange={setFormData}
            onOpenMembers={() => setTab('members')}
          />
        </TabsContent>

        <TabsContent value="customization">
          <div className="space-y-4">
            <CustomizationTab organization={organization} canEdit={canEdit} />
            <KnowledgeSharingCard organization={organization} canEdit={canEdit} />
          </div>
        </TabsContent>
        <TabsContent value="members">
          <OrganizationMembers organization={organization} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="templates">
          <OrganizationTemplates />
        </TabsContent>

      </Tabs>

      <ConfirmationModal
        open={showSectorChangeConfirm}
        title={t('organizations.modals.edit.alerts.sectorChangeTitle', 'Confirmer le changement de secteur')}
        message={t(
          'organizations.modals.edit.alerts.sectorChangeResetsKnowledge',
          "Changer le secteur va écraser la configuration de connaissance existante avec les fichiers par défaut de ce secteur. Continuer ?"
        )}
        confirmText={t('global.confirm', 'Confirmer')}
        cancelText={t('global.cancel')}
        onConfirm={handleConfirmSectorChange}
        onCancel={() => setShowSectorChangeConfirm(false)}
        loading={saving}
      />
    </PageLayout>
  );
};

export default MyOrganizationPage;
